/**
 * The destination's words (D4): one sentence per server resolution, and the
 * staff-only facts behind it. Lookups over server-derived state; nothing here
 * inspects a URL or decides joinability.
 *
 * The sentence says how the session is joined. The room is the band's fact
 * (object checklist item 4, each fact once); a session with no recorded room
 * says so here, once, because the band then has no place to show.
 */
import { MEETING_PROVIDER_LABEL } from "@/lib/api/courses";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { STAFF_DESTINATION_ISSUE, formatInCourseZone } from "@/lib/session-portal";

export function destinationStatement(
  workspace: SessionWorkspace,
  teaching: boolean,
): string {
  const { destination, session } = workspace;
  const provider = destination.meeting_provider
    ? MEETING_PROVIDER_LABEL[destination.meeting_provider]
    : "the remote destination";

  switch (destination.resolution) {
    case "join_ready":
      // A learner's Join act already names the provider; the sentence does not say it twice.
      return !teaching && destination.join_label ? "Remote access is ready." : `Remote access is ready through ${provider}.`;
    case "in_person":
      return destination.location
        ? "This session meets in person."
        : "This session meets in person. No room is recorded.";
    case "needs_destination":
      return teaching
        ? "This session has no working meeting link yet."
        : "The session destination is not available yet.";
    case "needs_repair":
      return teaching
        ? "The meeting link saved for this session cannot be used safely."
        : "The session destination is not available yet.";
    // The band carries the session's state (Cancelled, Held); these say only
    // what the missing join act means.
    case "cancelled_no_destination":
      return "There is nothing to join.";
    case "none":
      return session.status === "held"
        ? "There is nothing left to join."
        : "There is nothing to join for this session.";
    default:
      return "The session destination is not available yet.";
  }
}

/**
 * The staff-only facts behind a destination: which record is effective, the
 * latest change and its evidence, organizer ownership, the exact repair, and
 * any managed provider lifecycle. A learner's payload carries none of these.
 * Each is said in a teacher's words: where the session meets and how learners
 * join, with the same guarantee (what is confirmed, what not to retry).
 */
export function staffDestinationDetails(workspace: SessionWorkspace): string[] {
  const { destination, course } = workspace;
  const details: string[] = [];

  if (destination.effective_source) {
    details.push(
      destination.effective_source === "session_override"
        ? "This session has its own meeting place, set for it alone."
        : "This session meets where the course usually meets.",
    );
  }
  if (destination.provenance) {
    const provenance = destination.provenance;
    details.push(
      `Where this session meets was last changed ${formatInCourseZone(
        provenance.occurred_at,
        course.timezone,
        { dateStyle: "medium", timeStyle: "short", timeZoneName: "short" },
      )}. ${provenance.evidence_basis === "provider_verified"
        ? "The meeting provider confirmed the change."
        : provenance.evidence_basis === "staff_assertion"
          ? "Staff entered the change; the meeting provider has not confirmed it."
          : "It comes from older records, and whether it was confirmed is not known."}`,
    );
    if (provenance.destination_host) {
      details.push(`Learners join at ${provenance.destination_host}.`);
    }
    details.push(
      provenance.organizer_ownership === "tenant_workspace"
        ? "Staff recorded that an institution account hosts the meeting."
        : provenance.organizer_ownership === "external_or_personal"
          ? "Staff recorded that a personal or outside account hosts the meeting."
          : "Nobody has recorded which account hosts the meeting.",
    );
  }
  if (destination.destination_issue) {
    details.push(STAFF_DESTINATION_ISSUE[destination.destination_issue]);
  }

  const meet = destination.managed_google_meet;
  if (meet) {
    details.push(
      meet.state === "pending"
        ? "The Google Meet link is still being created. Do not start another."
        : meet.state === "failed"
          ? "The Google Meet link could not be created. Try again where it is offered, or paste a meeting link yourself."
          : meet.state === "unknown"
            ? "It is not known whether the Google Meet link was created. Do not try again. Paste a meeting link yourself, and ask an administrator to check Google Meet."
            : meet.state === "ready"
              ? "The Google Meet link was created automatically and is ready."
              : "No Google Meet link is created automatically for this session.",
    );
  }

  const zoom = destination.managed_zoom;
  if (zoom) {
    details.push(
      zoom.state === "pending"
        ? "The Zoom meeting is still being created. Do not start another."
        : zoom.state === "failed"
          ? "The Zoom meeting could not be created. Fix it only with the options offered here."
          : zoom.state === "unknown"
            ? "It is not known whether the Zoom meeting was created. Do not try again. Paste a meeting link yourself, and ask an administrator to check Zoom."
            : zoom.state === "ready" && zoom.schedule_state === "drifted"
              ? "The Zoom meeting is ready, but its time differs from this session. Zoom has not been changed automatically."
              : zoom.state === "ready"
                ? "The Zoom meeting was created automatically and matches this session's time."
                : zoom.manual_destination
                  ? "This session uses a Zoom link pasted by hand."
                  : "No Zoom meeting is created automatically for this session.",
    );
    if (zoom.shared_host_conflict) {
      details.push("Another session overlaps this one on the same Zoom account.");
    }
  }

  return details;
}
